import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import {
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { sharedMutators } from "../../../shared/mutators";
import { queries } from "../../../shared/queries";
import {
  parseStoredSkillFile,
  skillFileDecodedBytes,
  SKILL_MD_PATH,
  type StoredSkillFile,
  validateSkillPath,
} from "../../../shared/skills/spec";
import { useMouseDownOutside } from "../../hooks/useMouseDownOutside";
import { useQuery } from "../../hooks/useQuery";
import { zIndexes } from "../../lib/zIndexes";
import { AnchoredPopover } from "../AnchoredPopover";
import { Button } from "../Button";
import { CodeEditor } from "../CodeEditor";
import { useCodeEditorSearch } from "../CodeEditor/useCodeEditorSearch";
import { ConfirmModal } from "../ConfirmModal";
import { Dropdown } from "../Dropdown";
import { FileRender } from "../FileRender";
import { ToggleButton, ToggleGroup } from "../HeadingToggle";
import { Icon } from "../Icon";
import {
  InlineInput,
  InlineInputButton,
  InlineInputForm,
} from "../InlineInput";
import { StepDetails } from "../StepDetails";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";
import {
  dataUrlForStoredFile,
  downloadStoredFile,
  readUpload,
} from "./skillFileIO";

const AddButton = styled(ToggleButton)`
  &&& {
    background: var(--control-bg);
    color: var(--control-fg);
  }
  &&&:hover:not(:disabled) {
    background: var(--control-bg);
    opacity: 0.8;
  }
  &&&:active:not(:disabled) {
    background: var(--control-bg);
  }
`;

const HeadingOpener = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  height: 24px;
  padding: 0 8px;
  border-radius: 9999px;
  background: var(--alpha4);

  &:hover {
    background: var(--alpha8);
  }
`;

const OpenerDivider = styled("div")`
  width: 0.5px;
  height: 100%;
  background: var(--border-alpha);
  flex-shrink: 0;
`;

const AddPopover = styled(AnchoredPopover)`
  top: anchor(bottom);
  right: anchor(right);
  margin-top: 4px;
  width: 300px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: var(--surface-primary);
  box-shadow: var(--elevation-high);
  outline: 0.5px solid var(--border-alpha);
  z-index: ${zIndexes.dropdown};
`;

const PathInput = styled(InlineInput)`
  &&& {
    padding-top: 8px;
    padding-bottom: 8px;
  }
`;

const Placeholder = styled("div")`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const EditorFrame = styled("div")`
  height: 100%;
  overflow: hidden;

  & .cm-editor,
  & .cm-scroller {
    height: 100%;
  }

  && .cm-editor,
  && .cm-content,
  && .cm-line,
  && .cm-scroller {
    font-size: 12px;
    line-height: 18px;
  }

  && .cm-content {
    padding: 24px;
  }
`;

const FooterRow = styled("div")`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const FooterBadge = styled("div")`
  display: flex;
  align-items: center;
  gap: 4px;
  height: 20px;
  padding: 0 8px;
  margin-right: 2px;
  background: var(--alpha4);
  border-radius: 38px;
  white-space: nowrap;
  flex-shrink: 0;
`;

type SkillFileRow = {
  path: string;
  blobHash: string;
};

export type SkillFileEditsHandle = {
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  save: () => Promise<boolean>;
};

type Props = {
  skillId: string;
  disabled: boolean;
  selectedPath: string;
  onSelectPath: (path: string) => void;
  onChange: () => void;
  onSkillMdChange: (content: string) => void;
  editsRef: React.RefObject<SkillFileEditsHandle | null>;
};

export function SkillFilesPanel({
  skillId,
  disabled,
  selectedPath,
  onSelectPath,
  onChange,
  onSkillMdChange,
  editsRef,
}: Props) {
  const z = useZero();
  const [files] = useQuery(queries.loadSkillFileIndex({ skillId }));
  const [selectedRow] = useQuery(
    queries.loadSkillFile({ skillId, path: selectedPath })
  );
  const replaceRef = useRef<HTMLInputElement>(null);
  const uploadRef = useRef<HTMLInputElement>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [savedTexts, setSavedTexts] = useState<Record<string, string>>({});
  const [addOpen, setAddOpen] = useState(false);
  const [newPath, setNewPath] = useState("");
  const [pathError, setPathError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [addContainerRef, addPopoverRef] = useMouseDownOutside(() =>
    setAddOpen(false)
  );

  const selectedFile = useMemo(
    // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
    () =>
      selectedRow?.blob
        ? parseStoredSkillFile(selectedRow.blob.content)
        : undefined,
    [selectedRow?.blob]
  );
  const isText = selectedFile?.encoding === "utf8";
  const savedText = isText && selectedFile ? selectedFile.content : "";
  const draft = drafts[selectedPath] ?? savedText;
  const search = useCodeEditorSearch();

  useEffect(() => {
    if (!isText || drafts[selectedPath] !== undefined) return;
    // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
    setSavedTexts((current) =>
      current[selectedPath] === savedText
        ? current
        : { ...current, [selectedPath]: savedText }
    );
  }, [drafts, isText, savedText, selectedPath]);

  useEffect(() => {
    if (files.length > 0 && !files.some((file) => file.path === selectedPath)) {
      onSelectPath(files[0]!.path);
    }
  }, [files, selectedPath, onSelectPath]);

  // Non-text files render through FileRender, which reads from a URL. Skill
  // files live in the blob store (no HTTP endpoint), so serve the bytes as a
  // data: URL typed by extension.
  const binaryUrl = useMemo(
    () =>
      selectedFile && selectedFile.encoding === "base64"
        ? dataUrlForStoredFile(selectedPath, selectedFile)
        : null,
    [selectedFile, selectedPath]
  );

  const save = async (
    path: string,
    file: StoredSkillFile
  ): Promise<boolean> => {
    setSaveError(null);
    setIsSaving(true);
    try {
      await z.mutate(
        sharedMutators.upsertSkillFile({
          skillId,
          path,
          content: file.content,
          encoding: file.encoding,
        })
      ).client;
      onChange();
      if (path === SKILL_MD_PATH && file.encoding === "utf8") {
        onSkillMdChange(file.content);
      }
      if (file.encoding === "utf8") {
        setSavedTexts((current) => ({ ...current, [path]: file.content }));
      }
      return true;
    } catch (error) {
      setSaveError(
        error instanceof Error ? error.message : "Could not save skill file"
      );
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  // Edits are held per file until the footer's Save; switching files keeps
  // them (close/Done route through the modal's save-before-closing prompt).
  const changedPaths = useMemo(
    () =>
      Object.keys(drafts)
        .filter((path) => drafts[path] !== savedTexts[path])
        .toSorted((a, b) => {
          if (a === SKILL_MD_PATH) return -1;
          if (b === SKILL_MD_PATH) return 1;
          return a.localeCompare(b);
        }),
    [drafts, savedTexts]
  );

  const changedFileIndex = changedPaths.indexOf(selectedPath);

  const goPrevFile = () => {
    if (changedPaths.length === 0) return;
    const start = changedFileIndex >= 0 ? changedFileIndex : 0;
    const prev = (start - 1 + changedPaths.length) % changedPaths.length;
    onSelectPath(changedPaths[prev]!);
  };

  const goNextFile = () => {
    if (changedPaths.length === 0) return;
    const start = changedFileIndex >= 0 ? changedFileIndex : -1;
    onSelectPath(changedPaths[(start + 1) % changedPaths.length]!);
  };

  const saveAll = async (): Promise<boolean> => {
    if (changedPaths.length === 0) return true;
    setSaveError(null);
    setIsSaving(true);
    try {
      await z.mutate(
        sharedMutators.upsertSkillFiles({
          skillId,
          files: changedPaths.map((path) => ({
            path,
            content: drafts[path]!,
            encoding: "utf8",
          })),
        })
      ).client;
      onChange();
      if (changedPaths.includes(SKILL_MD_PATH)) {
        onSkillMdChange(drafts[SKILL_MD_PATH]!);
      }
      setSavedTexts((current) => ({
        ...current,
        ...Object.fromEntries(
          changedPaths.map((path) => [path, drafts[path]!])
        ),
      }));
      setDrafts((current) => {
        const next = { ...current };
        for (const path of changedPaths) delete next[path];
        return next;
      });
      return true;
    } catch (error) {
      setSaveError(
        error instanceof Error ? error.message : "Could not save skill files"
      );
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const selectFile = (path: string) => onSelectPath(path);

  const replace = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    await save(selectedPath, await readUpload(file));
  };

  const remove = async () => {
    if (selectedPath === SKILL_MD_PATH) return;
    await z.mutate(
      sharedMutators.deleteSkillFile({ skillId, path: selectedPath })
    ).client;
    onChange();
  };

  const addFile = async () => {
    const path = newPath.trim();
    try {
      validateSkillPath(path);
      if (files.some((file) => file.path === path)) {
        throw new Error("A file already exists at this path");
      }
    } catch (error) {
      setPathError(
        error instanceof Error ? error.message : "Invalid file path"
      );
      return;
    }
    setPathError(null);
    setNewPath("");
    setAddOpen(false);
    await save(path, { content: "", encoding: "utf8" });
    onSelectPath(path);
  };

  const upload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploads = [...(event.target.files ?? [])];
    event.target.value = "";
    setAddOpen(false);
    let lastPath: string | null = null;
    for (const file of uploads) {
      const path = file.webkitRelativePath || file.name;
      await save(path, await readUpload(file));
      lastPath = path;
    }
    if (lastPath) onSelectPath(lastPath);
  };

  const hasUnsavedChanges = !disabled && changedPaths.length > 0;

  // No dependency array: the parent reads this handle imperatively on close,
  // so it must always reflect the latest drafts.
  useImperativeHandle(editsRef, () => ({
    hasUnsavedChanges,
    isSaving,
    save: saveAll,
  }));

  const fileButton = (row: SkillFileRow, label: string) => ({
    type: "button" as const,
    key: row.path,
    label,
    left: (
      <Icon
        name={
          row.path === selectedPath && selectedFile?.encoding === "base64"
            ? "package-2"
            : "notebook-text"
        }
        size={16}
      />
    ),
    right:
      row.path === selectedPath ? (
        <Icon name="check" size={12} color="var(--text-secondary)" />
      ) : undefined,
    onClick: () => selectFile(row.path),
  });

  // Top-level files stay flat; files in subdirectories collapse into one
  // drill-in folder per directory so long paths show short names.
  const flatFileItems = files
    .filter((file) => !file.path.includes("/"))
    .toSorted((a, b) => {
      if (a.path === SKILL_MD_PATH) return -1;
      if (b.path === SKILL_MD_PATH) return 1;
      return a.path.localeCompare(b.path);
    })
    .map((file) => fileButton(file, file.path));

  const filesByDir = new Map<string, SkillFileRow[]>();
  for (const file of files) {
    if (!file.path.includes("/")) continue;
    const dir = file.path.slice(0, file.path.lastIndexOf("/"));
    const existing = filesByDir.get(dir);
    if (existing) existing.push(file);
    else filesByDir.set(dir, [file]);
  }
  const folderItems = [...filesByDir.entries()]
    .toSorted(([a], [b]) => a.localeCompare(b))
    .map(([dir, dirFiles]) => ({
      type: "submenu" as const,
      key: dir,
      label: dir,
      left: <Icon name="folder" size={16} />,
      selected: selectedPath.startsWith(`${dir}/`),
      submenu: [
        {
          items: dirFiles
            .toSorted((a, b) => a.path.localeCompare(b.path))
            .map((file) => fileButton(file, file.path.slice(dir.length + 1))),
        },
      ],
    }));

  return (
    <search.Provider>
      <StepDetails
        heading={
          <Dropdown
            align="bottom-left"
            opener={
              <HeadingOpener>
                <Text
                  variant="text-small-md"
                  color="var(--text-primary)"
                  style={{ padding: "0 2px" }}
                >
                  {files.length}
                </Text>
                <OpenerDivider />
                <span style={{ display: "flex" }}>
                  <Icon name="code" size={12} />
                </span>
                <Text variant="text-small-md" ellipsis>
                  {selectedPath}
                </Text>
                <Icon name="chevron-down" size={12} />
              </HeadingOpener>
            }
            sections={[
              { label: "Files", items: [...flatFileItems, ...folderItems] },
            ]}
          />
        }
        headingRight={
          <ToggleGroup>
            {search.toggleButton}
            {selectedFile &&
              (isText ? (
                <Tooltip content="Download">
                  <ToggleButton
                    onPress={() =>
                      downloadStoredFile(selectedPath, selectedFile)
                    }
                  >
                    <Icon name="circle-arrow-down" size={12} />
                  </ToggleButton>
                </Tooltip>
              ) : (
                <Tooltip content="Replace">
                  <ToggleButton
                    onPress={() => replaceRef.current?.click()}
                    disabled={disabled}
                  >
                    <Icon name="upload" size={12} />
                  </ToggleButton>
                </Tooltip>
              ))}
            {selectedFile && selectedPath !== SKILL_MD_PATH && (
              <Tooltip content="Delete">
                <ToggleButton
                  onPress={() => setConfirmDelete(true)}
                  disabled={disabled}
                >
                  <Icon name="trash" size={12} />
                </ToggleButton>
              </Tooltip>
            )}
            <div ref={addContainerRef} style={{ display: "contents" }}>
              <AddPopover
                isOpen={addOpen}
                direction="down"
                popoverRef={addPopoverRef}
                trigger={(anchorProps) => (
                  <Tooltip content="Add files">
                    <AddButton
                      {...anchorProps}
                      disabled={disabled}
                      aria-label="Add files"
                      onPress={() => setAddOpen((open) => !open)}
                    >
                      <Icon name="plus" size={12} />
                    </AddButton>
                  </Tooltip>
                )}
              >
                <Button
                  variant="subtle"
                  size="l"
                  icon="upload"
                  disabled={disabled}
                  onClick={() => uploadRef.current?.click()}
                  style={{
                    width: "100%",
                    justifyContent: "center",
                    borderRadius: 9999,
                  }}
                >
                  Upload files
                </Button>
                <InlineInputForm
                  onSubmit={(event) => {
                    event.preventDefault();
                    void addFile();
                  }}
                >
                  <PathInput
                    value={newPath}
                    disabled={disabled}
                    placeholder="references/example.md"
                    onChange={(event) => {
                      setNewPath(event.target.value);
                      setPathError(null);
                    }}
                  />
                  <InlineInputButton
                    type="submit"
                    disabled={disabled || !newPath.trim()}
                    aria-label="Add file"
                  >
                    <Icon name="arrow-right" size={16} />
                  </InlineInputButton>
                </InlineInputForm>
                {pathError && (
                  <Text variant="text-small" color="var(--red-600)">
                    {pathError}
                  </Text>
                )}
              </AddPopover>
            </div>
          </ToggleGroup>
        }
        subheader={search.subheader ?? undefined}
        notice={
          saveError ? (
            <Text variant="text-small" color="var(--red-600)">
              {saveError}
            </Text>
          ) : undefined
        }
        footer={
          hasUnsavedChanges ? (
            <>
              <FooterRow>
                <FooterBadge>
                  <Text variant="text-small-md" color="var(--text-primary)">
                    Edited files
                  </Text>
                  <Text variant="text-small" color="var(--text-secondary)">
                    {changedFileIndex >= 0 ? changedFileIndex + 1 : 0} /{" "}
                    {changedPaths.length}
                  </Text>
                </FooterBadge>
                {changedPaths.length >= 2 && (
                  <>
                    <Tooltip content="Previous file">
                      <ToggleButton onPress={goPrevFile}>
                        <Icon name="arrow-up" size={12} />
                      </ToggleButton>
                    </Tooltip>
                    <Tooltip content="Next file">
                      <ToggleButton onPress={goNextFile}>
                        <Icon name="arrow-down" size={12} />
                      </ToggleButton>
                    </Tooltip>
                  </>
                )}
              </FooterRow>
              <Button
                variant="primary"
                size="m"
                disabled={isSaving}
                onClick={() => void saveAll()}
              >
                Save
              </Button>
            </>
          ) : undefined
        }
        flushBody={isText}
        body={
          !selectedFile ? (
            <Placeholder>
              <Text variant="text-small" color="var(--text-secondary)">
                Select a file.
              </Text>
            </Placeholder>
          ) : isText ? (
            <EditorFrame>
              <CodeEditor
                key={selectedPath}
                value={draft}
                filePath={selectedPath}
                readOnly={disabled}
                showLineNumbers={false}
                onChange={(value) =>
                  setDrafts((current) => ({
                    ...current,
                    [selectedPath]: value,
                  }))
                }
              />
            </EditorFrame>
          ) : binaryUrl ? (
            <FileRender
              url={binaryUrl}
              fileName={selectedPath}
              sizeBytes={skillFileDecodedBytes(selectedFile)}
              bleed={12}
            />
          ) : null
        }
      />
      <input
        ref={replaceRef}
        type="file"
        style={{ display: "none" }}
        onChange={replace}
      />
      <input
        ref={uploadRef}
        type="file"
        multiple
        style={{ display: "none" }}
        onChange={upload}
      />
      <ConfirmModal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => {
          void remove();
          setConfirmDelete(false);
        }}
        title="Delete file?"
        description={`Permanently deletes ${selectedPath}.`}
        confirmLabel="Delete"
        destructive
      />
    </search.Provider>
  );
}
